import {
    ArrowLeftIcon,
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    EyeIcon,
    TruckIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import PhoneActions from '../../components/PhoneActions';

const StatusBadge = ({ status }) => {
  const statusType = status || 'active';
  const styles = {
    active: 'bg-success-100 text-success-800 border-success-200',
    available: 'bg-success-100 text-success-800 border-success-200',
    inactive: 'bg-gray-100 text-gray-800 border-gray-200',
    sold: 'bg-secondary-100 text-secondary-800 border-secondary-200',
    pending: 'bg-warning-100 text-warning-800 border-warning-200',
    blocked: 'bg-danger-100 text-danger-800 border-danger-200'
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${styles[statusType] || styles.active}`}>
      {statusType.charAt(0).toUpperCase() + statusType.slice(1)}
    </span>
  );
};

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCarDetails();
  }, [id]);

  const fetchCarDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCar(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to fetch car details:', error);
      toast.error('Failed to load car details');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockCar = async () => {
    if (!window.confirm('Are you sure you want to block this car listing?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/cars/${id}/block`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Car blocked successfully');
      fetchCarDetails();
    } catch (error) {
      console.error('Failed to block car:', error);
      toast.error('Failed to block car');
    }
  };

  const handleUnblockCar = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/cars/${id}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Car unblocked successfully');
      fetchCarDetails();
    } catch (error) {
      console.error('Failed to unblock car:', error);
      toast.error('Failed to unblock car');
    }
  };

  const handleDeleteCar = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this car listing? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Car deleted successfully');
      navigate('/cars');
    } catch (error) {
      console.error('Failed to delete car:', error);
      toast.error(error.response?.data?.message || 'Failed to delete car');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cars')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900">
              Car Not Found
            </h1>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-8 shadow-soft border border-gray-200 text-center"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-2">Car not found</h3>
          <p className="text-gray-600">The car you're looking for doesn't exist or has been removed.</p>
        </motion.div>
      </div>
    );
  }

  const locationStr = typeof car.location === 'object' && car.location
    ? `${car.location.city || ''}, ${car.location.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '')
    : car.location || 'Not specified';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cars')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900">
              {car.title || car.name || 'Untitled Car'}
            </h1>
            <p className="text-gray-600 mt-2">
              View detailed information about this car listing
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {car.status === 'blocked' ? (
            <button
              onClick={handleUnblockCar}
              className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
            >
              Unblock Car
            </button>
          ) : (
            <button
              onClick={handleBlockCar}
              className="px-4 py-2 bg-warning-600 text-white rounded-lg hover:bg-warning-700 transition-colors"
            >
              Block Car
            </button>
          )}
          <button
            onClick={handleDeleteCar}
            className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors"
          >
            Delete Car
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden"
          >
            <div className="relative">
              <img
                src={(car.images && car.images[0] && (car.images[0].url || car.images[0])) || 'https://via.placeholder.com/800x600'}
                alt={car.title || car.name}
                className="w-full h-96 object-cover"
              />
              <div className="absolute top-4 right-4">
                <StatusBadge status={car.status} />
              </div>
            </div>
            {car.images && car.images.length > 1 && (
              <div className="p-4 grid grid-cols-4 gap-4">
                {car.images.slice(1, 5).map((image, index) => (
                  <img
                    key={index}
                    src={image.url || image}
                    alt={`${car.title} ${index + 2}`}
                    className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                  />
                ))}
              </div>
            )}
          </motion.div>

          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Brand</p>
                <p className="text-lg font-semibold text-gray-900">{car.brand || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Model</p>
                <p className="text-lg font-semibold text-gray-900">{car.model || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Year</p>
                <p className="text-lg font-semibold text-gray-900">{car.year || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Price</p>
                <p className="text-lg font-semibold text-primary-600">₹{((car.price || 0) / 100000).toFixed(1)}L</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Fuel Type</p>
                <p className="text-lg font-semibold text-gray-900">{car.fuelType || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Transmission</p>
                <p className="text-lg font-semibold text-gray-900">{car.transmission || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Mileage</p>
                <p className="text-lg font-semibold text-gray-900">{car.mileage ? `${(car.mileage / 1000).toFixed(0)}K km` : 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Location</p>
                <p className="text-lg font-semibold text-gray-900">{locationStr}</p>
              </div>
            </div>
          </motion.div>

          {/* Description */}
          {car.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 leading-relaxed">{car.description}</p>
            </motion.div>
          )}

          {/* Features */}
          {car.features && car.features.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">Features</h2>
              <div className="grid grid-cols-2 gap-3">
                {car.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Seller Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Seller Information</h2>
            {car.seller ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {car.seller.firstName && car.seller.lastName 
                        ? `${car.seller.firstName} ${car.seller.lastName}`
                        : car.seller.businessName || car.seller.name || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 capitalize">{car.seller.role || 'Seller'}</p>
                  </div>
                </div>
                {car.seller.email && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>📧</span>
                    <span>{car.seller.email}</span>
                  </div>
                )}
                {car.seller.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <PhoneActions phone={car.seller.phone} textClassName="text-gray-600 font-normal" />
                  </div>
                )}
                {car.seller.sellerType && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Seller Type</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {car.seller.sellerType.replace('-', ' ')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No seller information available</p>
            )}
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Performance Stats</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <EyeIcon className="w-5 h-5 text-primary-600" />
                  <span className="text-gray-700">Views</span>
                </div>
                <span className="font-semibold text-gray-900">{car.views || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-5 h-5 text-secondary-600" />
                  <span className="text-gray-700">Inquiries</span>
                </div>
                <span className="font-semibold text-gray-900">{car.inquiries || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <TruckIcon className="w-5 h-5 text-success-600" />
                  <span className="text-gray-700">Test Drives</span>
                </div>
                <span className="font-semibold text-gray-900">{car.testDrives || 0}</span>
              </div>
            </div>
          </motion.div>

          {/* Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm">
                <CalendarIcon className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Listed on:</span>
                <span className="font-medium text-gray-900">
                  {new Date(car.createdAt || car.listingDate).toLocaleDateString()}
                </span>
              </div>
              {car.updatedAt && (
                <div className="flex items-center space-x-2 text-sm">
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Last updated:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(car.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {car.approvedAt && (
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircleIcon className="w-4 h-4 text-success-600" />
                  <span className="text-gray-600">Approved on:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(car.approvedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;